import api from './api'

// Network coverage types matching the backend API
export interface NetworkCoverageData {
  network: string
  has_2g: boolean
  has_3g: boolean
  has_4g: boolean
  has_5g: boolean
  indoor_coverage?: string | null
  outdoor_coverage?: string | null
  data_speed?: string | null
  error?: string | null
}

export interface NetworkStatusData {
  network: string
  has_issue: boolean
  issue_description?: string | null
  last_checked?: string | null
  error?: string | null
}

export interface NetworkCheckResult {
  postcode: string
  postcode_valid: boolean
  coverage: Record<string, NetworkCoverageData>
  status: Record<string, NetworkStatusData>
  timestamp?: string
}

export interface CoverageOnlyResult {
  postcode: string
  postcode_valid: boolean
  coverage: Record<string, NetworkCoverageData>
  cached?: boolean
}

export interface StatusOnlyResult {
  postcode: string
  postcode_valid: boolean
  status: Record<string, NetworkStatusData>
  cached?: boolean
}

// Legacy interface for backward compatibility
export interface CoverageResult {
  postcode: string
  coverage: {
    network: string
    available: boolean
    signal_strength: 'excellent' | 'good' | 'fair' | 'poor' | 'none'
    speed_estimate: string
    technology: '5G' | '4G' | '3G' | 'N/A'
  }[]
  recommended_products: {
    id: number
    name: string
    network: string
    price: number
  }[]
}

export interface AddressLookupResult {
  postcode: string
  addresses: {
    line1: string
    line2?: string
    city: string
    county?: string
  }[]
}

/**
 * Check network coverage and status for all UK networks
 * Uses the new isolated net_coverage service
 * Note: Uses extended timeout as coverage checks can take up to 2 minutes
 */
export const checkNetworkAll = async (postcode: string, recaptchaToken?: string): Promise<NetworkCheckResult> => {
  const response = await api.post('/network/check', { postcode, recaptcha_token: recaptchaToken }, { timeout: 185000 }) // > nginx 180s so axios never aborts a slow fallback first
  return response.data
}

/**
 * Check network coverage only (faster, no status check)
 */
export const checkCoverageOnly = async (postcode: string): Promise<CoverageOnlyResult> => {
  const response = await api.post('/network/coverage', { postcode }, { timeout: 120000 }) // 2 min timeout
  return response.data
}

/**
 * Check network status/outages only
 */
export const checkStatusOnly = async (postcode: string): Promise<StatusOnlyResult> => {
  const response = await api.post('/network/status', { postcode }, { timeout: 120000 }) // 2 min timeout
  return response.data
}

/**
 * Check coverage for a single network
 */
export const checkSingleNetworkCoverage = async (
  postcode: string,
  network: 'three' | 'vodafone' | 'o2' | 'ee'
): Promise<NetworkCoverageData> => {
  const response = await api.post(`/network/coverage/${network}`, { postcode }, { timeout: 60000 }) // 1 min timeout
  return response.data.coverage
}

// Legacy function - maintained for backward compatibility
export const checkCoverage = async (postcode: string) => {
  const response = await api.post('/network/check', { postcode })
  return response.data
}

export const getAvailableNetworks = async () => {
  return ['three', 'vodafone', 'o2' /*, 'ee'*/] // EE hidden — uncomment to re-enable
}

export const getRecommendedProducts = async (postcode: string, network?: string) => {
  const response = await api.get('/network-coverage/products', {
    params: { postcode, network }
  })
  return response.data
}

// Address Lookup (Ideal Postcodes)
export const lookupAddress = async (postcode: string) => {
  const response = await api.get('/addresses/lookup', {
    params: { postcode }
  })
  return response.data as { success: boolean; result: AddressLookupResult }
}

export const autocompleteAddress = async (query: string) => {
  const response = await api.get('/addresses/autocomplete', {
    params: { query }
  })
  return response.data
}

// Helper function to get best technology available
export const getBestTechnology = (coverage: NetworkCoverageData): string => {
  if (coverage.has_5g) return '5G'
  if (coverage.has_4g) return '4G'
  if (coverage.has_3g) return '3G'
  if (coverage.has_2g) return '2G'
  return 'No Coverage'
}

// Helper to get signal quality based on coverage
export const getSignalQuality = (coverage: NetworkCoverageData): 'excellent' | 'good' | 'fair' | 'poor' | 'none' => {
  if (coverage.has_5g) return 'excellent'
  if (coverage.has_4g && coverage.indoor_coverage === 'good') return 'excellent'
  if (coverage.has_4g) return 'good'
  if (coverage.has_3g) return 'fair'
  if (coverage.has_2g) return 'poor'
  return 'none'
}
